import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bug,
  Check,
  Copy,
  Cpu,
  GitPullRequest,
  KeyRound,
  Mail,
  MessageSquareWarning,
  ShieldCheck,
  SlidersHorizontal,
  Terminal,
  Users,
  Wrench,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PACKAGE = '@kolanut/heal';
const INSTALL = `npm install ${PACKAGE}`;
const ACCESS_URL = '/book-demo';

const LOOP = [
  { icon: MessageSquareWarning, title: 'Customers report it', body: 'Survey answers, support replies and reviews flow in. No ticket triage required.' },
  { icon: Bug, title: 'Signals become issues', body: 'Feedback is grouped by the underlying problem, ranked by severity and customers affected.' },
  { icon: Cpu, title: 'A sandbox spins up', body: 'Your repo, already installed, forks into an isolated VM in about a second.' },
  { icon: Wrench, title: 'An agent fixes it', body: 'It reproduces the bug, finds the root cause, writes the fix and a test, and runs your suite.' },
  { icon: GitPullRequest, title: 'You review a PR', body: 'Diff, test results, live before/after previews and screenshots. You merge, or you take over.' },
  { icon: Mail, title: 'Customers hear back', body: 'The people who reported it get a "we fixed it" email the moment it ships.' },
];

const TERMINAL_LINES = [
  { kind: 'muted', text: '# 3 customers: "promo code never works on my phone"' },
  { kind: 'cmd', text: 'heal ▸ forking workspace computer (0.9s)' },
  { kind: 'tool', text: '› Read src/cart.js' },
  { kind: 'tool', text: '› Grep "PROMO_CODES["' },
  { kind: 'text', text: 'Root cause: codes are matched case-sensitively; mobile keyboards send "welcome10".' },
  { kind: 'tool', text: '› Edit src/cart.js  (normalize to upper case)' },
  { kind: 'tool', text: '› Edit test/cart.test.js  (+ lowercase code test)' },
  { kind: 'tool', text: '› Bash npm test' },
  { kind: 'ok', text: '✓ 6 passing' },
  { kind: 'ok', text: '✓ before/after previews live · PR #12 ready for review' },
];

const CODE = `import { heal } from '${PACKAGE}';

heal.init({
  repo: 'github.com/acme/web-app',
  signals: ['surveys', 'support'],
  autonomy: 'fix',            // observe | diagnose | fix | pr
  tests: 'npm test',
  preview: { command: 'npm run dev', port: 3000 },
});

// Anywhere in your product: capture feedback with who said it
heal.feedback({ email: user.email, message: form.comment });`;

const SAFETY = [
  { icon: Cpu, title: 'One VM per fix', body: 'Every attempt runs in its own throwaway Linux VM. Nothing touches your laptop, CI or production.' },
  { icon: KeyRound, title: 'Keys never enter the sandbox', body: 'GitHub and model credentials are injected at the network edge. Code in the VM cannot read them.' },
  { icon: SlidersHorizontal, title: 'You set the autonomy', body: 'Start at Observe or Diagnose. Move to Fix or Auto PR when you trust it. Humans always merge.' },
  { icon: ShieldCheck, title: 'Customer data stays out', body: 'Emails and phone numbers are stripped before feedback reaches a model or a sandbox.' },
];

const FAQ = [
  {
    q: 'What does the agent actually change?',
    a: 'Only the repositories you connect, only on its own branch. It opens a pull request for review and never pushes to your default branch.',
  },
  {
    q: 'What if the fix is only 80% right?',
    a: 'Take over in a shared live session: your team joins the same sandbox, files and terminal the agent used, finishes the change together and pushes to the same PR.',
  },
  {
    q: 'Which stacks does it support?',
    a: 'Anything that runs on Linux. Node, Python, Go, Ruby and Docker Compose apps work out of the box; setup and test commands are auto-detected or set in one line.',
  },
  {
    q: 'Do I need Kolanut for the feedback side?',
    a: 'No. Pipe in feedback from your own forms with heal.feedback(), or connect Kolanut surveys and support to get the full loop including the "we fixed it" emails.',
  },
];

const termColor = {
  muted: 'text-white/40',
  cmd: 'text-[#c4ffa0]',
  tool: 'text-sky-300',
  text: 'text-white/80',
  ok: 'text-[#8ae690]',
};

const InstallCommand = ({ dark = false }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked; the command is visible to select manually
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`group inline-flex w-full sm:w-auto items-center justify-between gap-4 rounded-full px-5 h-12 md:h-14 font-mono text-sm md:text-base transition-all ${
        dark
          ? 'border border-white/25 bg-white/10 text-white hover:bg-white/15 backdrop-blur-sm'
          : 'border border-gray-200 bg-gray-950 text-white hover:bg-gray-900'
      }`}
      aria-label={`Copy ${INSTALL}`}
    >
      <span>
        <span className="text-[#8ae690]">$</span> {INSTALL}
      </span>
      {copied ? <Check className="w-4 h-4 text-[#8ae690]" /> : <Copy className="w-4 h-4 opacity-60 group-hover:opacity-100" />}
    </button>
  );
};

const AnimatedTerminal = () => {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    if (visible >= TERMINAL_LINES.length) {
      const reset = setTimeout(() => setVisible(0), 4000);
      return () => clearTimeout(reset);
    }
    const timer = setTimeout(() => setVisible((v) => v + 1), visible === 0 ? 600 : 750);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <div className="w-full rounded-2xl border border-white/10 bg-black/60 shadow-2xl backdrop-blur-md overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="ml-3 font-mono text-[11px] text-white/40">agent run · fix</span>
      </div>
      <div className="min-h-[18rem] px-4 py-4 font-mono text-[12.5px] leading-relaxed">
        {TERMINAL_LINES.slice(0, visible).map((line, i) => (
          <motion.div
            key={`${i}-${line.text}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className={`${termColor[line.kind]} break-words`}
          >
            {line.text}
          </motion.div>
        ))}
        {visible < TERMINAL_LINES.length && <span className="inline-block h-3.5 w-2 animate-pulse bg-white/50 align-middle" />}
      </div>
    </div>
  );
};

const CheckoutMock = ({ fixed }) => (
  <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700">
      <span className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${fixed ? 'bg-emerald-500' : 'bg-rose-500'}`} />
        {fixed ? 'After · agent fix' : 'Before · main'}
      </span>
      <span className="font-mono text-[10px] text-gray-400">{fixed ? 'kln-7f3a.style.dev' : 'kln-7f3a-before.style.dev'}</span>
    </div>
    <div className="space-y-3 p-5 text-sm text-gray-800">
      <p className="font-semibold">Checkout</p>
      <div className="flex gap-2">
        <div className="flex-1 rounded-md border border-gray-300 px-3 py-2 font-mono text-xs">welcome10</div>
        <div className="rounded-md bg-gray-900 px-3 py-2 text-xs text-white">Apply</div>
      </div>
      <p className={`text-xs ${fixed ? 'text-emerald-700' : 'text-rose-700'}`}>
        {fixed ? '10% off applied.' : 'That promo code is not valid.'}
      </p>
      <div className="space-y-1.5 border-t border-gray-100 pt-3 text-xs">
        <div className="flex justify-between"><span>Subtotal</span><span>$32.00</span></div>
        <div className="flex justify-between"><span>Discount</span><span>{fixed ? '$3.20' : '$0.00'}</span></div>
        <div className="flex justify-between font-semibold text-sm"><span>Total</span><span>{fixed ? '$33.80' : '$37.00'}</span></div>
      </div>
    </div>
  </div>
);

const SectionHeader = ({ eyebrow, title, description }) => (
  <div className="mx-auto max-w-2xl text-center">
    <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-gray-500">{eyebrow}</p>
    <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight text-black">{title}</h2>
    {description && <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">{description}</p>}
  </div>
);

const HealingPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow pb-20 sm:pb-0">
        {/* Hero */}
        <section className="bg-white pt-2 sm:pt-3 pb-4 sm:pb-5">
          <div className="px-2 sm:px-3 md:px-4">
            <div className="relative isolate overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] bg-[#0b0f0c]">
              <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
                  backgroundSize: '44px 44px',
                }}
                aria-hidden
              />
              <div className="absolute -top-40 -right-32 h-[30rem] w-[30rem] rounded-full bg-[#8ae690]/25 blur-3xl" aria-hidden />
              <div className="absolute -bottom-48 -left-24 h-[26rem] w-[26rem] rounded-full bg-[#c4ffa0]/10 blur-3xl" aria-hidden />

              <div className="relative z-10 grid gap-10 px-5 py-14 sm:px-10 sm:py-16 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-12 lg:px-14 lg:py-20">
                <div className="text-center sm:text-left">
                  <p className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-white/95 backdrop-blur-sm">
                    <Terminal className="w-3.5 h-3.5" /> Kolanut Heal · npm package
                  </p>
                  <h1 className="mt-5 font-serif text-[2.35rem] leading-[1.1] text-white sm:mt-6 sm:text-5xl md:text-[3.25rem] lg:text-6xl lg:leading-[1.08]">
                    Your customers report the bug.
                    <br className="hidden sm:block" />
                    <span className="text-[#8ae690]"> Your codebase heals itself.</span>
                  </h1>
                  <p className="mt-4 max-w-lg text-lg leading-relaxed text-white/80 sm:mt-5 sm:text-xl mx-auto sm:mx-0">
                    One package turns customer feedback into tested fixes. An agent reproduces the problem in a private
                    sandbox, fixes it, and hands your team a pull request with live before and after previews.
                  </p>
                  <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                    <InstallCommand dark />
                    <a
                      href={ACCESS_URL}
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 md:h-14 px-8 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] transition-all"
                    >
                      Get early access <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                  <p className="mt-4 text-xs text-white/50">Works with GitHub · Node, Python, Go, Ruby, Docker · Humans always merge</p>
                </div>
                <AnimatedTerminal />
              </div>
            </div>
          </div>
        </section>

        {/* Loop */}
        <section id="how" className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader
              eyebrow="The healing loop"
              title="From complaint to merged fix, without a ticket"
              description="Most bugs are reported by customers long before an engineer sees them. Heal closes that gap end to end."
            />
            <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {LOOP.map((step, i) => (
                <motion.li
                  key={step.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="relative rounded-2xl border border-gray-200 bg-white p-6"
                >
                  <span className="absolute right-5 top-5 font-mono text-xs text-gray-300">0{i + 1}</span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#f4fbf4] text-black ring-1 ring-[#c4ffa0]">
                    <step.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-black">{step.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{step.body}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* Package */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-[#f4fbf4] to-white border-y border-[#c4ffa0]/25">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-10 lg:grid-cols-[1fr_1.15fr] lg:items-center">
            <div>
              <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-gray-500">Built for teams</p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight text-black">Ten lines. Then it just runs.</h2>
              <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                Install the package, point it at your repo and your feedback, and pick how much the agent may do. Setup,
                tests and previews are auto-detected; every fix lands as a normal pull request your team already knows how
                to review.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-gray-700">
                {[
                  'Repo cloned and dependencies installed once, then snapshotted',
                  'Fixes run in parallel, each in its own sandbox',
                  'Before/after screenshots and live links on every PR',
                  'A live dashboard of every fix in flight',
                ].map((item) => (
                  <li key={item} className="flex gap-2.5">
                    <Check className="mt-0.5 w-4 h-4 shrink-0 text-emerald-600" /> {item}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <InstallCommand />
              </div>
            </div>
            <div className="rounded-2xl bg-gray-950 shadow-xl overflow-hidden">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                <span className="font-mono text-xs text-white/50">heal.config.js</span>
                <span className="rounded-full bg-[#8ae690]/15 px-2 py-0.5 font-mono text-[10px] text-[#8ae690]">{PACKAGE}</span>
              </div>
              <pre className="overflow-x-auto px-5 py-5 font-mono text-[12.5px] leading-relaxed text-white/85">
                <code>{CODE}</code>
              </pre>
            </div>
          </div>
        </section>

        {/* Before / after */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <SectionHeader
              eyebrow="Proof, not promises"
              title="See the fix before you merge it"
              description="Every fix ships with two live links, the old app and the fixed one, plus screenshots of the agent replaying exactly what your customer did."
            />
            <div className="mt-12 grid gap-5 sm:grid-cols-2">
              <CheckoutMock fixed={false} />
              <CheckoutMock fixed />
            </div>
          </div>
        </section>

        {/* Safety */}
        <section className="py-16 md:py-24 bg-[#0b0f0c] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-white/50">Safe by design</p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight">An agent with its own computer, not yours</h2>
            </div>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {SAFETY.map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <item.icon className="w-6 h-6 text-[#8ae690]" />
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/65">{item.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center sm:flex-row sm:text-left">
              <Users className="w-8 h-8 shrink-0 text-[#c4ffa0]" />
              <p className="text-sm text-white/75">
                <span className="font-semibold text-white">Human takeover, built in.</span> When a fix needs judgement, open the
                agent&apos;s sandbox as a shared live session. Your team works in the same files, terminal and preview, then
                pushes to the same PR.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <SectionHeader eyebrow="Questions" title="What teams ask first" />
            <div className="mt-10 divide-y divide-gray-200 rounded-2xl border border-gray-200">
              {FAQ.map((item) => (
                <details key={item.q} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-black">
                    {item.q}
                    <span className="text-gray-400 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 border-t border-[#c4ffa0]/25 bg-gradient-to-b from-[#f4fbf4] to-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight text-black">Let your product fix what customers feel.</h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
              Heal is in early access for product and engineering teams. Bring a repo; we will run the first fix with you.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={ACCESS_URL}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 md:h-14 px-8 md:px-12 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
                  <img src="/kolanut_logo.png" alt="" className="w-4 h-4 object-contain" />
                </span>
                Get early access
              </a>
              <InstallCommand />
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white/40 backdrop-blur-md border-t border-gray-200/40 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] sm:hidden z-40">
        <a
          href={ACCESS_URL}
          className="flex items-center justify-center w-full py-3.5 rounded-full bg-[#8ae690] text-black font-bold text-base shadow-lg hover:bg-[#7ad67f] active:scale-[0.98] transition-all duration-200"
        >
          Get early access
        </a>
      </div>
    </div>
  );
};

export default HealingPage;
